/**
 * Chat, as data: who we can talk to (ALL · ALLY · a private line to each other human), what a
 * typed line means (slash commands: `/a` ALL, `/t` ALLY, `/w NAME …` private, `/r …` reply to
 * the last private message; a command with no text switches the channel; `/mute NAME` and
 * `/unmute NAME` silence a player on this client for the room), the message log (the last
 * LOG_SHOWN lines fade LOG_FADE_MS after they came — a line with a map point LOG_LOC_FADE_MS, so
 * its `[F7]` can still be clicked; the whole scrollback while the input is open) and grid
 * references for map points (`LOC F7`). Pure (tested in test/chat.test.ts); the
 * panel is ui/chat.ts, the relay is the server's (protocol chat.ts).
 */
import { CHAT_MAX, cleanChatText, type ChatChannel, type ChatLoc, type ChatMessage } from '@sa/protocol';
import type { PlayerId } from '@sa/sim';

export type ChatTarget = { channel: 'all' } | { channel: 'ally' } | { channel: 'pm'; to: PlayerId };

export interface ChatPeer {
  id: PlayerId;
  name: string;
  isBot: boolean;
}

/** What the input knows about the match (lobby: no ids, ALL only). */
export interface ChatContext {
  me: PlayerId;
  peers: readonly ChatPeer[];
  /** an ALLY channel exists (an ally, teammates, or the ally of when one of us fell) */
  hasAlly: boolean;
  /** the last player who wrote to us privately */
  lastPm: PlayerId | null;
  lobby: boolean;
}

export const ALL: ChatTarget = { channel: 'all' };
export const ALLY: ChatTarget = { channel: 'ally' };

export const sameTarget = (a: ChatTarget, b: ChatTarget) => a.channel === b.channel && (a.channel !== 'pm' || a.to === (b as { to: PlayerId }).to);

/** The channel chip: `ALL`, `ALLY`, `→ KESTREL`. */
export function targetLabel(t: ChatTarget, nameOf: (p: PlayerId) => string): string {
  return t.channel === 'all' ? 'ALL' : t.channel === 'ally' ? 'ALLY' : `→ ${nameOf(t.to)}`;
}

/** Humans we may write to privately (bots cannot read; not ourselves). */
const pmPeers = (ctx: ChatContext) => ctx.peers.filter((p) => !p.isBot && p.id !== ctx.me);

/** The channels ↑ / ↓ step through: ALL, ALLY (when there is one), then each other human. */
export function availableTargets(ctx: ChatContext): ChatTarget[] {
  if (ctx.lobby) return [ALL];
  return [ALL, ...(ctx.hasAlly ? [ALLY] : []), ...pmPeers(ctx).map((p): ChatTarget => ({ channel: 'pm', to: p.id }))];
}

/** The next (`dir` 1) or previous (−1) channel after `cur`; one no longer available starts over at ALL. */
export function cycleTarget(cur: ChatTarget, ctx: ChatContext, dir: 1 | -1): ChatTarget {
  const list = availableTargets(ctx);
  const i = list.findIndex((t) => sameTarget(t, cur));
  if (i < 0) return ALL;
  return list[(i + dir + list.length) % list.length];
}

/** `cur` still makes sense (an ally broke off, a player left): else ALL. */
export function validTarget(cur: ChatTarget, ctx: ChatContext): ChatTarget {
  return availableTargets(ctx).some((t) => sameTarget(t, cur)) ? cur : ALL;
}

export type ChatInput =
  | { kind: 'none' }
  | { kind: 'send'; target: ChatTarget; text: string }
  /** a command without text: talk on this channel from now on */
  | { kind: 'switch'; target: ChatTarget }
  /** `/mute NAME` · `/unmute NAME` (name: as the players are called; null: list who is muted) */
  | { kind: 'mute'; name: string | null; on: boolean }
  | { kind: 'error'; msg: string };

export const CHAT_HELP = '/A ALL · /T ALLY · /W NAME PRIVATE · /R REPLY · /MUTE NAME';

/** What the channel chip previews while a line is typed: where it would go, or why it cannot. */
export type ChatPreview = { target: ChatTarget } | { error: string };

/**
 * The channel a half-typed line would go to — `/t …` ALLY, `/a …` ALL, `/r …` the last private
 * writer, `/w al…` the one player whose name it starts (an unknown name: `NO SUCH PLAYER`) — so
 * the chip says it before Enter. Anything else (no command, a command still being typed, an
 * ambiguous name, `/mute`): the current channel.
 */
export function previewTarget(raw: string, cur: ChatTarget, ctx: ChatContext): ChatPreview {
  const line = raw.replace(/^\s+/, '');
  if (!line.startsWith('/') || ctx.lobby) return { target: cur };
  const parts = line.split(/\s+/);
  const cmd = parts[0].toLowerCase();
  const typedCmd = parts.length > 1;
  switch (cmd) {
    case '/a':
    case '/all':
      return { target: ALL };
    case '/t':
    case '/ally':
    case '/team':
      return ctx.hasAlly ? { target: ALLY } : { error: 'NO ALLY' };
    case '/r':
      return ctx.lastPm !== null ? { target: { channel: 'pm', to: ctx.lastPm } } : { error: 'NOBODY TO REPLY TO' };
    case '/w':
    case '/pm':
    case '/msg': {
      const who = parts[1];
      if (!typedCmd || !who) return { target: cur };
      const w = who.toUpperCase();
      const all = ctx.peers.filter((p) => p.id !== ctx.me);
      const exact = all.filter((p) => p.name.toUpperCase() === w);
      const hits = exact.length ? exact : all.filter((p) => p.name.toUpperCase().startsWith(w));
      if (hits.length === 0) return { error: 'NO SUCH PLAYER' };
      if (hits.length > 1) return { target: cur };
      if (hits[0].isBot) return { error: `${hits[0].name.toUpperCase()} IS A BOT` };
      return { target: { channel: 'pm', to: hits[0].id } };
    }
    default:
      return { target: cur };
  }
}

/** A player by name: exact (any case), else the one name it starts. */
function findPeer(ctx: ChatContext, word: string): ChatPeer | { error: string } {
  const w = word.toUpperCase();
  const all = ctx.peers.filter((p) => p.id !== ctx.me);
  const exact = all.filter((p) => p.name.toUpperCase() === w);
  const hits = exact.length ? exact : all.filter((p) => p.name.toUpperCase().startsWith(w));
  if (hits.length === 0) return { error: `NO PLAYER NAMED ${w}` };
  if (hits.length > 1) return { error: `${w}: ${hits.map((p) => p.name.toUpperCase()).join(' · ')}?` };
  if (hits[0].isBot) return { error: `${hits[0].name.toUpperCase()} IS A BOT · BOTS CANNOT READ` };
  return hits[0];
}

/** What the typed line means, talking on `cur` (see the file comment). */
export function parseChatInput(raw: string, cur: ChatTarget, ctx: ChatContext): ChatInput {
  const line = raw.trim();
  if (!line) return { kind: 'none' };
  let target = cur;
  let text = line;
  if (line.startsWith('/')) {
    const [cmd, ...rest] = line.split(/\s+/);
    switch (cmd.toLowerCase()) {
      case '/mute':
      case '/unmute': {
        const on = cmd.toLowerCase() === '/mute';
        const who = rest[0];
        if (!who) return { kind: 'mute', name: null, on };
        // the lobby knows names only: muted as typed
        if (ctx.lobby) return { kind: 'mute', name: who.toUpperCase(), on };
        const all = ctx.peers.filter((p) => p.id !== ctx.me);
        const w = who.toUpperCase();
        const exact = all.filter((p) => p.name.toUpperCase() === w);
        const hits = exact.length ? exact : all.filter((p) => p.name.toUpperCase().startsWith(w));
        if (hits.length === 1) return { kind: 'mute', name: hits[0].name.toUpperCase(), on };
        return { kind: 'error', msg: hits.length ? `${w}: ${hits.map((p) => p.name.toUpperCase()).join(' · ')}?` : `NO PLAYER NAMED ${w}` };
      }
      case '/a':
      case '/all':
        target = ALL;
        break;
      case '/t':
      case '/ally':
      case '/team':
        if (ctx.lobby) return { kind: 'error', msg: 'LOBBY CHAT IS ALL ONLY' };
        if (!ctx.hasAlly) return { kind: 'error', msg: 'NO ALLY TO TELL' };
        target = ALLY;
        break;
      case '/w':
      case '/pm':
      case '/msg': {
        if (ctx.lobby) return { kind: 'error', msg: 'LOBBY CHAT IS ALL ONLY' };
        const who = rest.shift();
        if (!who) return { kind: 'error', msg: 'WHO? /W NAME MESSAGE' };
        const p = findPeer(ctx, who);
        if ('error' in p) return { kind: 'error', msg: p.error };
        target = { channel: 'pm', to: p.id };
        break;
      }
      case '/r':
        if (ctx.lastPm === null) return { kind: 'error', msg: 'NOBODY TO REPLY TO' };
        target = { channel: 'pm', to: ctx.lastPm };
        break;
      default:
        return { kind: 'error', msg: CHAT_HELP };
    }
    text = rest.join(' ');
    if (!text.trim()) return { kind: 'switch', target };
  } else if (!availableTargets(ctx).some((t) => sameTarget(t, target))) {
    return { kind: 'error', msg: target.channel === 'ally' ? 'NO ALLY TO TELL' : 'THEY ARE GONE · ↑↓ ANOTHER CHANNEL' };
  }
  const clean = cleanChatText(text);
  if (clean === null) return text.trim() ? { kind: 'error', msg: `${CHAT_MAX} CHARACTERS AT MOST` } : { kind: 'none' };
  return { kind: 'send', target, text: clean };
}

// ------------------------------------------------------------------------------- the log

export interface ChatLine {
  id: number;
  /** ms (performance.now) it came */
  at: number;
  /** a line of ours, someone's, or the panel's own (an error) */
  kind: 'mine' | 'theirs' | 'sys';
  channel: ChatChannel;
  from: PlayerId;
  name: string;
  to?: PlayerId;
  toName?: string;
  text: string;
  loc?: ChatLoc;
}

/** lines shown while the input is closed, and how long they stay (ms; with a map point: longer) */
export const LOG_SHOWN = 6;
export const LOG_FADE_MS = 12_000;
export const LOG_LOC_FADE_MS = 30_000;
const lifeOf = (l: ChatLine) => (l.loc ? LOG_LOC_FADE_MS : LOG_FADE_MS);
/** the last stretch of a line's life fades out (ms) */
export const LOG_FADE_OUT_MS = 1_500;
const LOG_KEEP = 100;

export class ChatLog {
  private list: ChatLine[] = [];
  private nextId = 1;
  /** the last player who wrote to us privately (`/r`) */
  lastPm: PlayerId | null = null;

  get lines(): readonly ChatLine[] {
    return this.list;
  }

  add(m: ChatMessage, mine: boolean, at: number): ChatLine {
    const line: ChatLine = {
      id: this.nextId++,
      at,
      kind: mine ? 'mine' : 'theirs',
      channel: m.channel,
      from: m.from,
      name: m.name,
      ...(m.to !== undefined ? { to: m.to } : {}),
      ...(m.toName !== undefined ? { toName: m.toName } : {}),
      text: m.text,
      ...(m.loc ? { loc: m.loc } : {}),
    };
    if (!mine && m.channel === 'pm') this.lastPm = m.from;
    return this.keep(line);
  }

  /** The panel's own line (an error, a refusal from the server). */
  system(text: string, at: number): ChatLine {
    return this.keep({ id: this.nextId++, at, kind: 'sys', channel: 'all', from: 0, name: '', text });
  }

  clear(): void {
    this.list = [];
    this.lastPm = null;
  }

  /** What the log shows at `now`: everything kept while open, else the last LOG_SHOWN not yet faded. */
  visible(now: number, open: boolean): ChatLine[] {
    if (open) return this.list.slice();
    return this.list.slice(-LOG_SHOWN).filter((l) => now - l.at < lifeOf(l));
  }

  private keep(line: ChatLine): ChatLine {
    this.list.push(line);
    if (this.list.length > LOG_KEEP) this.list.splice(0, this.list.length - LOG_KEEP);
    return line;
  }
}

/** A closed log line's opacity at `now` (1 → 0 over its last LOG_FADE_OUT_MS). */
export function lineAlpha(line: ChatLine, now: number): number {
  return Math.max(0, Math.min(1, (lifeOf(line) - (now - line.at)) / LOG_FADE_OUT_MS));
}

/** grid columns (no I / O: they read as 1 / 0) */
const COLS = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const ROWS = 12;

/** A map point as a grid reference, `F7`: 24 columns A–Z (no I, O) west → east, 12 rows north → south. */
export function gridRef(x: number, y: number, width: number, height: number): string {
  const c = Math.max(0, Math.min(COLS.length - 1, Math.floor((x / width) * COLS.length)));
  const r = Math.max(0, Math.min(ROWS - 1, Math.floor((y / height) * ROWS)));
  return `${COLS[c]}${r + 1}`;
}
